// Emisión de comprobantes de retención (codDoc 07). Financiero → Gestión CxP → Emitir Retención.
// Proveedor (sujeto retenido) → facturas recibidas (docsSustento) → conceptos renta/IVA.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const field = 'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const usd = (v: number) => v.toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });
const hoyPeriodo = (() => { const d = new Date(); return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; })();
const urlDoc = (r: string | null | undefined) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);

const ESTADO: Record<string, { label: string; cls: string }> = {
  por_emitir: { label: 'Por emitir', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  encolado: { label: 'Encolado', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  autorizado: { label: 'Autorizado', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  devuelto: { label: 'Devuelto', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  rechazado: { label: 'Rechazado', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};
const EMITIBLE = ['por_emitir', 'rechazado', 'devuelto', 'error'];
const IMP = { '1': 'RENTA', '2': 'IVA', '6': 'ISD' } as Record<string, string>;

interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }
interface FactRec { serie: string; fecha_emision: string; clave_acceso: string | null; total_sin_impuestos: number; iva: number; importe_total: number }
interface CodRet { codigo_impuesto: string; codigo_retencion: string; descripcion: string; porcentaje: number }
interface LineaRet { key: number; codigo: string; codigo_retencion: string; base_imponible: number; porcentaje_retener: number }
interface DocSust { key: number; serie: string; fecha: string; clave: string | null; total_sin_impuestos: number; iva: number; importe_total: number; lineas: LineaRet[] }
let _k = 1;

export function Retenciones() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<'nueva' | 'lista'>('nueva');
  const [editId, setEditId] = useState<string | null>(null);
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Emitir Retención</h1>
        <div className="flex gap-1 rounded-lg border p-1">
          {([['nueva', editId ? 'Editar' : 'Nueva'], ['lista', 'Retenciones']] as const).map(([k, t]) => (
            <button key={k} onClick={() => { if (k === 'nueva' && vista === 'lista') setEditId(null); setVista(k); }} className={`rounded-md px-3 py-1 text-sm ${vista === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
      </div>
      {vista === 'nueva'
        ? <Nueva editId={editId} onCreada={() => { setEditId(null); setVista('lista'); void qc.invalidateQueries({ queryKey: ['ret-list'] }); }} />
        : <Lista onEdit={(id) => { setEditId(id); setVista('nueva'); }} />}
    </div>
  );
}

function Nueva({ editId, onCreada }: { editId: string | null; onCreada: () => void }) {
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [] });
  const proveedores = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('proveedor')), [partners]);
  const { data: codigos = [] } = useQuery<CodRet[]>({ queryKey: ['ret-codigos'], queryFn: async () => (await apiClient.post('/rpc/ret_codigos', {}, FIN)).data || [] });
  const codKey = (c: CodRet) => `${c.codigo_impuesto}|${c.codigo_retencion}`;
  const codMap = useMemo(() => Object.fromEntries(codigos.map((c) => [codKey(c), c])), [codigos]);

  const [proveedorId, setProveedorId] = useState('');
  const [periodo, setPeriodo] = useState(hoyPeriodo);
  const [obs, setObs] = useState('');
  const [docs, setDocs] = useState<DocSust[]>([]);
  const [facturaSel, setFacturaSel] = useState('');

  // Modo edición: cargar la retención y precargar el formulario
  const { data: edit } = useQuery({
    queryKey: ['ret-obtener', editId], enabled: !!editId,
    queryFn: async () => (await apiClient.post('/rpc/ret_obtener', { _id: editId }, FIN)).data,
  });
  useEffect(() => {
    if (!edit) return;
    setProveedorId(edit.partner_id || ''); setPeriodo(edit.periodo_fiscal || hoyPeriodo); setObs(edit.observaciones || '');
    setDocs((edit.docs || []).map((d: { num_doc_sustento?: string; fecha_emision_doc_sustento?: string; num_aut_doc_sustento?: string; total_sin_impuestos?: number; importe_total?: number; lineas?: Array<{ codigo?: string; codigo_retencion?: string; base_imponible?: number; porcentaje_retener?: number }> }) => ({
      key: _k++, serie: d.num_doc_sustento || '', fecha: d.fecha_emision_doc_sustento ? String(d.fecha_emision_doc_sustento).slice(0, 10) : '',
      clave: d.num_aut_doc_sustento || null, total_sin_impuestos: Number(d.total_sin_impuestos) || 0,
      iva: Math.max(0, (Number(d.importe_total) || 0) - (Number(d.total_sin_impuestos) || 0)), importe_total: Number(d.importe_total) || 0,
      lineas: (d.lineas || []).map((l) => ({ key: _k++, codigo: l.codigo || '', codigo_retencion: l.codigo_retencion || '', base_imponible: Number(l.base_imponible) || 0, porcentaje_retener: Number(l.porcentaje_retener) || 0 })),
    })));
  }, [edit]);

  const { data: facturas = [] } = useQuery<FactRec[]>({
    queryKey: ['ret-fact', proveedorId], enabled: !!proveedorId,
    queryFn: async () => (await apiClient.post('/rpc/ret_facturas_recibidas', { _partner_id: proveedorId }, FIN)).data || [],
  });

  const agregarDoc = () => {
    const f = facturas.find((x) => x.serie === facturaSel);
    if (!f) return;
    if (docs.some((d) => d.serie === f.serie)) { toast.error('Esa factura ya está agregada'); return; }
    setDocs((ds) => [...ds, { key: _k++, serie: f.serie, fecha: String(f.fecha_emision).slice(0, 10), clave: f.clave_acceso, total_sin_impuestos: Number(f.total_sin_impuestos) || 0, iva: Number(f.iva) || 0, importe_total: Number(f.importe_total) || 0, lineas: [] }]);
    setFacturaSel('');
  };
  const setDoc = (key: number, patch: Partial<DocSust>) => setDocs((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const addLinea = (dk: number) => setDoc(dk, { lineas: [...docs.find((d) => d.key === dk)!.lineas, { key: _k++, codigo: '', codigo_retencion: '', base_imponible: 0, porcentaje_retener: 0 }] });
  const setLinea = (dk: number, lk: number, patch: Partial<LineaRet>) => {
    const d = docs.find((x) => x.key === dk)!;
    setDoc(dk, { lineas: d.lineas.map((l) => (l.key === lk ? { ...l, ...patch } : l)) });
  };
  const elegirCodigo = (dk: number, lk: number, key: string) => {
    const c = codMap[key]; const d = docs.find((x) => x.key === dk)!;
    if (!c) { setLinea(dk, lk, { codigo: '', codigo_retencion: '', porcentaje_retener: 0 }); return; }
    const base = c.codigo_impuesto === '2' ? d.iva : d.total_sin_impuestos;
    setLinea(dk, lk, { codigo: c.codigo_impuesto, codigo_retencion: c.codigo_retencion, porcentaje_retener: Number(c.porcentaje), base_imponible: base });
  };
  const valorLinea = (l: LineaRet) => Number(((l.base_imponible || 0) * (l.porcentaje_retener || 0) / 100).toFixed(2));
  const totalRetenido = docs.reduce((s, d) => s + d.lineas.reduce((a, l) => a + valorLinea(l), 0), 0);

  const crear = useMutation({
    mutationFn: async () => {
      const _docs = docs.filter((d) => d.lineas.length > 0).map((d) => ({
        num_doc_sustento: d.serie, fecha_emision_doc_sustento: d.fecha, num_aut_doc_sustento: d.clave,
        total_sin_impuestos: d.total_sin_impuestos, importe_total: d.importe_total, cod_sustento: '01',
        lineas: d.lineas.filter((l) => l.codigo).map((l) => ({ codigo: l.codigo, codigo_retencion: l.codigo_retencion, base_imponible: l.base_imponible, porcentaje_retener: l.porcentaje_retener, valor_retenido: valorLinea(l) })),
      }));
      if (_docs.length === 0 || _docs.every((d) => d.lineas.length === 0)) throw new Error('Agregue al menos una factura con un concepto de retención');
      const _cab = { partner_id: proveedorId, periodo_fiscal: periodo, observaciones: obs || null };
      return editId
        ? (await apiClient.post('/rpc/ret_actualizar', { _id: editId, _cab, _docs }, FIN)).data
        : (await apiClient.post('/rpc/ret_crear', { _cab, _docs }, FIN)).data;
    },
    onSuccess: () => { toast.success(editId ? 'Retención actualizada' : 'Retención creada'); onCreada(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Datos de cabecera</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Proveedor (sujeto retenido) *</label>
            <select className={field} value={proveedorId} onChange={(e) => { setProveedorId(e.target.value); setDocs([]); }}>
              <option value="">— Selecciona —</option>
              {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Período fiscal (MM/AAAA) *</label>
            <input className={field} value={periodo} onChange={(e) => setPeriodo(e.target.value)} placeholder="MM/AAAA" />
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-muted-foreground">Observación</label>
            <input className={field} value={obs} onChange={(e) => setObs(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {proveedorId && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Facturas a retener ({docs.length})</CardTitle>
            <div className="flex items-center gap-2">
              <select className={`${field} w-72`} value={facturaSel} onChange={(e) => setFacturaSel(e.target.value)}>
                <option value="">— Factura recibida —</option>
                {facturas.map((f) => <option key={f.serie} value={f.serie}>{f.serie} · {String(f.fecha_emision).slice(0, 10)} · {usd(Number(f.importe_total))}</option>)}
              </select>
              <Button size="sm" variant="outline" disabled={!facturaSel} onClick={agregarDoc}><Plus className="w-4 h-4 mr-1" /> Agregar</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {docs.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Elige una factura recibida del proveedor y agrégala.</p>}
            {docs.map((d) => (
              <div key={d.key} className="rounded-lg border p-3">
                <div className="flex items-center justify-between text-sm">
                  <div><span className="font-medium">{d.serie}</span> <span className="text-muted-foreground">· {d.fecha} · base {usd(d.total_sin_impuestos)} · IVA {usd(d.iva)}</span></div>
                  <button className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1" onClick={() => setDocs((ds) => ds.filter((x) => x.key !== d.key))}><Trash2 className="w-4 h-4" /></button>
                </div>
                <table className="w-full text-sm mt-2">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2 min-w-64">Concepto</th><th className="py-1 pr-2">Base</th><th className="py-1 pr-2">%</th><th className="py-1 pr-2 text-right">Valor</th><th></th>
                  </tr></thead>
                  <tbody>
                    {d.lineas.map((l) => (
                      <tr key={l.key} className="border-b last:border-0">
                        <td className="py-1 pr-2">
                          <select className={field} value={l.codigo ? `${l.codigo}|${l.codigo_retencion}` : ''} onChange={(e) => elegirCodigo(d.key, l.key, e.target.value)}>
                            <option value="">— Concepto —</option>
                            <optgroup label="Renta">{codigos.filter((c) => c.codigo_impuesto === '1').map((c) => <option key={codKey(c)} value={codKey(c)}>{c.codigo_retencion} · {c.descripcion} ({c.porcentaje}%)</option>)}</optgroup>
                            <optgroup label="IVA">{codigos.filter((c) => c.codigo_impuesto === '2').map((c) => <option key={codKey(c)} value={codKey(c)}>{c.codigo_retencion} · {c.descripcion} ({c.porcentaje}%)</option>)}</optgroup>
                          </select>
                        </td>
                        <td className="py-1 pr-2"><input type="number" step="0.01" className={`${field} w-28`} value={l.base_imponible} onChange={(e) => setLinea(d.key, l.key, { base_imponible: Number(e.target.value) })} /></td>
                        <td className="py-1 pr-2"><input type="number" step="0.01" className={`${field} w-20`} value={l.porcentaje_retener} onChange={(e) => setLinea(d.key, l.key, { porcentaje_retener: Number(e.target.value) })} /></td>
                        <td className="py-1 pr-2 text-right whitespace-nowrap font-medium">{usd(valorLinea(l))}</td>
                        <td className="py-1"><button className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1" onClick={() => setDoc(d.key, { lineas: d.lineas.filter((x) => x.key !== l.key) })}><Trash2 className="w-4 h-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => addLinea(d.key)}><Plus className="w-4 h-4 mr-1" /> Concepto ({IMP['1']}/{IMP['2']})</Button>
              </div>
            ))}
            <div className="flex justify-end text-sm font-semibold">Total retenido: {usd(totalRetenido)}</div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button isLoading={crear.isPending} disabled={!proveedorId || docs.length === 0} onClick={() => crear.mutate()}>{editId ? 'Guardar cambios' : 'Generar retención'}</Button>
      </div>
    </div>
  );
}

interface RetRow {
  id: string; fecha_emision: string; periodo_fiscal: string | null; proveedor: string | null; ruc_sujeto: string | null;
  total_retenido: number; docs: number; estado_emision: string; mensaje_sri?: string | null; serie_comprobante?: string | null; xml_url?: string | null; ride_url?: string | null;
}

function Lista({ onEdit }: { onEdit: (id: string) => void }) {
  const qc = useQueryClient();
  const { data: docs = [], isFetching } = useQuery<RetRow[]>({
    queryKey: ['ret-list'], queryFn: async () => (await apiClient.post('/rpc/ret_listar', { _estado: 'todos' }, FIN)).data || [],
    refetchInterval: (q) => (q.state.data || []).some((d: RetRow) => d.estado_emision === 'encolado') ? 2500 : false,
  });
  const emitir = useMutation({ mutationFn: async (id: string) => (await apiClient.post('/rpc/ret_emitir', { _id: id }, FIN)).data,
    onSuccess: () => { toast.success('Retención encolada'); void qc.invalidateQueries({ queryKey: ['ret-list'] }); }, onError: (e) => toast.error(getErrorMessage(e)) });
  const eliminar = useMutation({ mutationFn: async (id: string) => (await apiClient.post('/rpc/ret_eliminar', { _id: id }, FIN)).data,
    onSuccess: () => { toast.success('Retención eliminada'); void qc.invalidateQueries({ queryKey: ['ret-list'] }); }, onError: (e) => toast.error(getErrorMessage(e)) });

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [prov, setProv] = useState('');
  const provs = useMemo(() => ([...new Set(docs.map((d) => d.proveedor).filter(Boolean))] as string[]).sort(), [docs]);
  const filtradas = useMemo(() => docs.filter((d) =>
    (!desde || d.fecha_emision >= desde) && (!hasta || d.fecha_emision <= hasta) && (!prov || d.proveedor === prov)), [docs, desde, hasta, prov]);

  const dlg = useDialogoEnLinea();

  return (
    <Card>
      {dlg.ui}
      <CardHeader className="pb-2">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">Retenciones ({filtradas.length})</CardTitle>
          <div className="flex items-end gap-2 flex-wrap text-xs text-muted-foreground">
            <label className="flex flex-col">Desde<input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
            <label className="flex flex-col">Hasta<input type="date" className="h-9 mt-1 rounded-md border border-input bg-background px-2 text-sm" value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
            <label className="flex flex-col">Proveedor
              <select className="h-9 mt-1 max-w-56 rounded-md border border-input bg-background px-2 text-sm" value={prov} onChange={(e) => setProv(e.target.value)}>
                <option value="">Todos</option>{provs.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></label>
            {(desde || hasta || prov) && <Button size="sm" variant="outline" onClick={() => { setDesde(''); setHasta(''); setProv(''); }}>Limpiar</Button>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">N° Documento</th><th className="py-2 pr-3">Proveedor</th><th className="py-2 pr-3">Período</th>
            <th className="py-2 pr-3 text-right">Retenido</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
          </tr></thead>
          <tbody>
            {filtradas.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin retenciones</td></tr>}
            {filtradas.map((d) => (
              <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                <td className="py-2 pr-3 whitespace-nowrap">{d.fecha_emision}</td>
                <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{d.serie_comprobante || '—'}</td>
                <td className="py-2 pr-3"><span className="truncate max-w-56 inline-block align-middle">{d.proveedor || '—'}</span></td>
                <td className="py-2 pr-3">{d.periodo_fiscal || '—'}</td>
                <td className="py-2 pr-3 text-right whitespace-nowrap font-medium">{usd(Number(d.total_retenido))}</td>
                <td className="py-2 pr-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${(ESTADO[d.estado_emision] || ESTADO.por_emitir).cls}`}>{(ESTADO[d.estado_emision] || ESTADO.por_emitir).label}</span>
                  {['devuelto', 'rechazado', 'error'].includes(d.estado_emision) && d.mensaje_sri && (
                    <button type="button" onClick={() => void dlg.avisar('Mensaje del SRI', d.mensaje_sri!)} title="Ver motivo completo"
                      className="mt-1 block max-w-xs text-left text-xs text-red-600 hover:underline">
                      ⚠ {d.mensaje_sri.length > 140 ? d.mensaje_sri.slice(0, 140) + '…' : d.mensaje_sri}
                    </button>
                  )}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  {urlDoc(d.ride_url) && <a href={urlDoc(d.ride_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><FileText className="w-4 h-4" /> RIDE</a>}
                  {urlDoc(d.xml_url) && <a href={urlDoc(d.xml_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mr-2 text-xs text-primary hover:underline align-middle"><Download className="w-4 h-4" /> XML</a>}
                  {EMITIBLE.includes(d.estado_emision) && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted mr-1 align-middle" title="Editar" onClick={() => onEdit(d.id)}><Pencil className="w-4 h-4" /></button>}
                  {EMITIBLE.includes(d.estado_emision) && <Button size="sm" isLoading={emitir.isPending} onClick={() => emitir.mutate(d.id)}>Emitir</Button>}
                  {d.estado_emision === 'encolado' && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> procesando…</span>}
                  {EMITIBLE.includes(d.estado_emision) && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1 align-middle" title="Eliminar" onClick={async () => { if (await dlg.confirmar('¿Eliminar esta retención?', { peligro: true })) eliminar.mutate(d.id); }}><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
